import { findTimeSuggestions, isTimeConflictIgnored, sceneTimeConflicts, timeConflictIgnore, weekdayConflict, wordingFix, type IgnoredTerm, type MarkAt, type MarkInfo, type StoryCtx, type StoryNode, type TimeConflict } from '@dispositio/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { PluginKey, type EditorState } from '@tiptap/pm/state';
import { suggestExtension, type SuggestAct, type SuggestLive, type SuggestState } from './suggest';
import { timeRanges, type TimeRange } from './timeMark';
import { editedTimes, timeGuardKey } from './timeMarkGuard';

/**
 * Zeitmarken, die nicht mehr stimmen (PF 9, PLAN.md §16d): rot an der Stelle (`suggest.ts`, Art `conflict`), mit Grund im Tooltip und
 * den Auswegen daneben – ✓ übernimmt den Vorschlag, ✕ merkt „ist Absicht“ im Dokument, dazwischen ein weiterer Ausweg. Der Text ändert
 * sich nur auf ✓.
 *  - verschoben bzw. Reihenfolge (core `sceneTimeConflicts`, nur im Draft einer Szene)
 *  - Wortlaut: Der Text sagt etwas anderes als die Marke – auch wenn direkt davor bzw. dahinter getippt wurde („vier“ vor „ Tage später“)
 *  - Wochentag: Der Text nennt einen anderen Wochentag, als das Datum hat (core `weekdayConflict`; in jedem Dokument)
 *  - geändert: In der Marke getippt, und ihr Text ist keine erkennbare Zeitangabe mehr (nur in dieser Sitzung, `timeMarkGuard`)
 */

export type ConflictKind = TimeConflict['kind'] | 'edited';

export interface ConflictHit {
  /** die Stelle; beim Wortlaut samt dem dazugetippten Text */
  from: number;
  to: number;
  /** die Zeitmarke selbst und ihr Text */
  mark: TimeRange & { text: string };
  conflict: TimeConflict | { kind: 'edited'; index: number };
}

export interface ConflictLive extends SuggestLive<ConflictHit> {
  ctx: StoryCtx;
  /** die Szene, deren Draft das ist; ohne: nur Wortlaut und geänderte Stellen */
  scene: StoryNode | null;
  ignored: readonly IgnoredTerm[] | undefined;
  title: (hit: ConflictHit) => string;
  hitLabels: (hit: ConflictHit) => { accept: string | null; ignore: string };
  other: (hit: ConflictHit) => { label: string; title: string } | null;
}

export const timeConflictKey = new PluginKey<SuggestState<ConflictHit>>('timeConflicts');

/** Eine Zeitmarke im Editor als Marke mit Text (für core) */
export const markAt = (m: ConflictHit['mark']): MarkAt => ({ value: m.value, precision: m.precision, approx: m.approx, role: m.role, text: m.text });

/**
 * Was die Erkennung an jeder Zeitmarke liest: die Zeitangabe im Absatz, die die Marke am meisten überdeckt und keine andere Marke berührt
 * (so zählt auch Text, der direkt davor bzw. dahinter getippt wurde).
 */
function heardAt(doc: PMNode, ranges: readonly TimeRange[]): ((MarkInfo & { from: number; to: number }) | null)[] {
  const byBlock = new Map<number, (MarkInfo & { from: number; to: number })[]>();
  return ranges.map((r) => {
    const $r = doc.resolve(r.from);
    const start = $r.start();
    let found = byBlock.get(start);
    if (!found) {
      const block = $r.parent;
      found = findTimeSuggestions(block.textBetween(0, block.content.size, '\n', '\n')).map((s) => ({ ...s, from: start + s.from, to: start + s.to }));
      byBlock.set(start, found);
    }
    const overlap = (s: { from: number; to: number }): number => Math.min(s.to, r.to) - Math.max(s.from, r.from);
    const own = found.filter((s) => overlap(s) > 0 && !ranges.some((o) => o !== r && o.from < s.to && o.to > s.from));
    return own.sort((a, b) => overlap(b) - overlap(a))[0] ?? null;
  });
}

function conflictHits(doc: PMNode, l: ConflictLive, state: EditorState): ConflictHit[] {
  const ranges = timeRanges(doc);
  if (ranges.length === 0) return [];
  const marks = ranges.map((r) => ({ ...r, text: doc.textBetween(r.from, r.to, ' ', '￼') }));
  const heard = heardAt(doc, ranges);
  const edited = editedTimes(state);
  const out: ConflictHit[] = [];
  const taken = new Set<number>();
  marks.forEach((m, i) => {
    const h = heard[i];
    const typed = edited.some((e) => e.from < m.to && e.to > m.from);
    const fix = wordingFix(markAt(m), h ?? null, typed);
    const wd = fix || !h ? null : weekdayConflict(markAt(m));
    if (fix && h) out.push({ from: Math.min(m.from, h.from), to: Math.max(m.to, h.to), mark: m, conflict: { kind: 'wording', index: i, fix } });
    else if (!h && typed) out.push({ from: m.from, to: m.to, mark: m, conflict: { kind: 'edited', index: i } });
    else if (wd) out.push({ from: m.from, to: m.to, mark: m, conflict: { kind: 'weekday', index: i, ...wd } });
    else return;
    taken.add(i);
  });
  if (l.scene) for (const c of sceneTimeConflicts(l.ctx, l.scene, marks.map(markAt))) if (!taken.has(c.index)) out.push({ from: marks[c.index]!.from, to: marks[c.index]!.to, mark: marks[c.index]!, conflict: c });
  return out
    .filter((h) => h.conflict.kind === 'edited' || !isTimeConflictIgnored(l.ignored, timeConflictIgnore(h.conflict, l.scene, markAt(h.mark))))
    .sort((a, b) => a.from - b.from);
}

export type ConflictAct = SuggestAct;

export const timeConflicts = (live: { current: ConflictLive }) =>
  suggestExtension<ConflictHit, ConflictLive>(
    {
      name: 'timeConflicts',
      kind: 'conflict',
      key: timeConflictKey,
      base: 'rt-conflict',
      hits: conflictHits,
      // der Wächter hat eine Stelle als geändert gemerkt bzw. vergessen
      refresh: (tr) => tr.getMeta(timeGuardKey) !== undefined,
      mark: (h, l) => ({ class: `rt-time-conflict ${h.conflict.kind}`, 'data-conflict': h.conflict.kind, title: l.title(h) }),
      labels: (h, l) => l.hitLabels(h),
      other: (h, l) => l.other(h),
      word: (h) => h.mark.text,
      testId: 'conflict',
    },
    live,
  );
